import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { CheckboxField } from "@/features/clients/components/record-form-fields";
import { propertiesApi } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import type { Property } from "@/features/worksheets/types";

interface PropertyFormValues {
  description: string;
  title: string;
  address: string;
  currentValue: string;
  loanBalance: string;
  monthlyPmt: string;
  finalPmtDate: string;
  lender: string;
  lenderContact: string;
  incomeGen: boolean;
  rentExp: string;
}

const validationSchema = Yup.object({
  description: Yup.string().required("Description is required"),
});

function toInitialValues(record: Property | null): PropertyFormValues {
  return {
    description: record?.description ?? "",
    title: record?.title ?? "",
    address: record?.address ?? "",
    currentValue: record ? centsToDollars(record.currentValue) : "",
    loanBalance: record ? centsToDollars(record.loanBalance) : "",
    monthlyPmt: record ? centsToDollars(record.monthlyPmt) : "",
    finalPmtDate: record?.finalPmtDate ?? "",
    lender: record?.lender ?? "",
    lenderContact: record?.lenderContact ?? "",
    incomeGen: record?.incomeGen ?? false,
    rentExp: record ? centsToDollars(record.rentExp) : "",
  };
}

function toPayload(v: PropertyFormValues): Record<string, unknown> {
  return {
    description: v.description,
    title: v.title,
    address: v.address,
    currentValue: dollarsToCents(v.currentValue),
    loanBalance: dollarsToCents(v.loanBalance),
    monthlyPmt: dollarsToCents(v.monthlyPmt),
    finalPmtDate: v.finalPmtDate || null,
    lender: v.lender,
    lenderContact: v.lenderContact,
    incomeGen: v.incomeGen,
    rentExp: dollarsToCents(v.rentExp),
  };
}

export function PropertyDialog({
  caseId,
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  caseId: string;
  record: Property | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  async function onSave(values: PropertyFormValues) {
    const payload = toPayload(values);
    if (record) {
      await propertiesApi.update(caseId, record.id, payload);
    } else {
      await propertiesApi.create(caseId, payload);
    }
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Property"
      initialValues={toInitialValues(record)}
      validationSchema={validationSchema}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField
        name="description"
        label="Description"
        labelClassName={FIELD_LABEL}
        placeholder="e.g. Primary residence"
      />
      <div className="grid grid-cols-2 gap-4">
        <FormField name="title" label="Title" labelClassName={FIELD_LABEL} />
        <FormField name="address" label="Address" labelClassName={FIELD_LABEL} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <MoneyField name="currentValue" label="Current Value" />
        <MoneyField name="loanBalance" label="Loan Balance" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <MoneyField name="monthlyPmt" label="Monthly Payment" />
        <FormField
          name="finalPmtDate"
          label="Final Payment Date"
          labelClassName={FIELD_LABEL}
          type="date"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField name="lender" label="Lender" labelClassName={FIELD_LABEL} />
        <FormField name="lenderContact" label="Lender Contact" labelClassName={FIELD_LABEL} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <MoneyField name="rentExp" label="Rental Expense" />
      </div>
      <CheckboxField name="incomeGen" label="Income-generating" />
    </CrudDialog>
  );
}
